import { useEffect, useState } from 'react';

const DISMISSAL_KEY = 'kudu-pwa-install-dismissed-at';
const FIRST_SEEN_KEY = 'kudu-pwa-first-seen-at';
const HOMEWORK_ENGAGEMENT_KEY = 'kudu-pwa-homework-engaged-at';
const DISMISSAL_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

function isIOS() {
  if (typeof navigator === 'undefined') {
    return false;
  }

  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function getStoredTimestamp(key: string) {
  const value = window.localStorage.getItem(key);
  const timestamp = value ? Number(value) : Number.NaN;
  return Number.isFinite(timestamp) ? timestamp : null;
}

function hasRecentDismissal() {
  const dismissedAt = getStoredTimestamp(DISMISSAL_KEY);
  return dismissedAt !== null && Date.now() - dismissedAt < DISMISSAL_WINDOW_MS;
}

function hasMeaningfulEngagement() {
  return getStoredTimestamp(HOMEWORK_ENGAGEMENT_KEY) !== null;
}

function hasReturnSession() {
  const firstSeenAt = getStoredTimestamp(FIRST_SEEN_KEY);

  if (firstSeenAt === null) {
    window.localStorage.setItem(FIRST_SEEN_KEY, String(Date.now()));
    return false;
  }

  return Date.now() - firstSeenAt > 60 * 1000;
}

export function markPwaHomeworkEngagement() {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(HOMEWORK_ENGAGEMENT_KEY, String(Date.now()));
}

export function useInstallPrompt(isStandalone: boolean) {
  const [promptEvent, setPromptEvent] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isEligible, setIsEligible] = useState(false);
  const isiOSDevice = isIOS();

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    setIsEligible(
      !isStandalone &&
        !hasRecentDismissal() &&
        (hasMeaningfulEngagement() || hasReturnSession()),
    );

    function handleBeforeInstallPrompt(event: Event) {
      event.preventDefault();
      setPromptEvent(event as BeforeInstallPromptEvent);
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () =>
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt,
      );
  }, [isStandalone]);

  async function promptInstall() {
    if (!promptEvent) {
      return;
    }

    await promptEvent.prompt();
    await promptEvent.userChoice;
    setPromptEvent(null);
  }

  function dismissInstallPrompt() {
    window.localStorage.setItem(DISMISSAL_KEY, String(Date.now()));
    setIsEligible(false);
    setPromptEvent(null);
  }

  return {
    canInstall: isEligible && promptEvent !== null && !isStandalone,
    dismissInstallPrompt,
    isIOSInstallEligible: isEligible && isiOSDevice && !isStandalone,
    promptInstall,
  };
}

